import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { Api } from '../../../../services/interceptor';
import { QUERY_KEY } from '../../../../services/queryEndPoint';
import {
  bulkUploadPlayersForTeams,
  type IBulkPlayerRequest,
  type IPlayerForTeamsItem,
} from '../../api/myLeaguesApi';
import {
  addPlayerToTeamSnapshot,
  checkDuplicatesInLeague,
  fetchDivisions,
  fetchTeamSnapshotPlayers,
  removePlayerFromTeamSnapshot,
  searchPlayers,
} from '../../../myTeam/api/myTeamApi';
import type { IMember } from '../../../myTeam/types/myTeam';
import { extractConfiguredDivisionList } from '../../utils/divisionResponses';
import type {
  IDivisionResponse,
  IDivisionTeamEntry,
  IPlayerSearchResult,
} from '../../../myTeam/types/myTeam';
import FormDropdown from '../../../../components/common/FormDropdown';
import { Images } from '../../../../utils/imageConstants';
import './AddPlayersStepV2.scss';

interface AddPlayersStepV2Props {
  leagueId: string | null;
  tournamentId?: string | null;
  teamSnapshots?: { divisionId: string; groupId: string; teamSnapshotId: string }[] | null;
  onNext: () => void;
}

interface TeamOption {
  id: string;
  label: string;
  baseTeamId?: string;
  teamSnapshotId?: string;
}
interface GroupOption {
  id: string;
  name: string;
  teams: TeamOption[];
}
interface DivisionOption {
  id: string;
  name: string;
  groups: GroupOption[];
}

interface NewPlayerRow {
  rowId: string;
  firstName: string;
  lastName: string;
  role: 'Player' | 'Captain' | 'Vice Captain';
  playingRole: 'Batsman' | 'Bowler' | 'All-rounder' | 'Wicket-keeper' | '';
  countryCode: string; // dial code without '+', e.g. '91'
  phoneNo: string; // digits only, no country code
  emailId: string;
  dob: string; // ISO yyyy-MM-dd
}

interface RowErrors {
  firstName?: string;
  lastName?: string;
  phoneNo?: string;
  emailId?: string;
  dob?: string;
}

interface CountryDial {
  code: string; // dial code without '+', '91'
  iso: string; // 'IN'
  name: string; // 'India'
  length: number | number[]; // expected national-number length
}

const COUNTRIES: CountryDial[] = [
  { code: '91', iso: 'IN', name: 'India', length: 10 },
  { code: '1', iso: 'US', name: 'United States', length: 10 },
  { code: '44', iso: 'GB', name: 'United Kingdom', length: [10, 11] },
  { code: '61', iso: 'AU', name: 'Australia', length: 9 },
  { code: '64', iso: 'NZ', name: 'New Zealand', length: [8, 9] },
  { code: '27', iso: 'ZA', name: 'South Africa', length: 9 },
  { code: '92', iso: 'PK', name: 'Pakistan', length: 10 },
  { code: '880', iso: 'BD', name: 'Bangladesh', length: 10 },
  { code: '94', iso: 'LK', name: 'Sri Lanka', length: 9 },
  { code: '971', iso: 'AE', name: 'UAE', length: 9 },
  { code: '974', iso: 'QA', name: 'Qatar', length: 8 },
  { code: '966', iso: 'SA', name: 'Saudi Arabia', length: 9 },
  { code: '65', iso: 'SG', name: 'Singapore', length: 8 },
  { code: '60', iso: 'MY', name: 'Malaysia', length: [9, 10] },
  { code: '1', iso: 'CA', name: 'Canada', length: 10 },
];

const DEFAULT_COUNTRY_CODE = '91';

const findCountry = (code: string): CountryDial | undefined =>
  COUNTRIES.find((c) => c.code === code);

const validatePhone = (digits: string, code: string): string | undefined => {
  const cleaned = digits.replace(/\D/g, '');
  if (!cleaned) return undefined; // optional field
  const country = findCountry(code);
  if (!country) return 'Pick country code';
  const valid = Array.isArray(country.length)
    ? country.length.includes(cleaned.length)
    : cleaned.length === country.length;
  if (!valid) {
    const expected = Array.isArray(country.length)
      ? country.length.join(' or ')
      : country.length;
    return `Expected ${expected} digits`;
  }
  return undefined;
};

const validateDob = (iso: string): string | undefined => {
  if (!iso) return 'Date of birth is required';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Invalid date';
  const today = new Date();
  if (d > today) return 'DOB cannot be in the future';
  const age = (today.getTime() - d.getTime()) / (365.25 * 24 * 3600 * 1000);
  if (age < 4) return 'Player must be at least 4 years old';
  if (age > 100) return 'Date is too far in the past';
  return undefined;
};

const ROLE_OPTIONS: NewPlayerRow['role'][] = ['Player', 'Captain', 'Vice Captain'];
const PLAYING_ROLE_OPTIONS: NewPlayerRow['playingRole'][] = [
  'Batsman',
  'Bowler',
  'All-rounder',
  'Wicket-keeper',
];

const ADMIN_ROLE_TO_API: Record<NewPlayerRow['role'], string> = {
  Player: 'PLAYER',
  Captain: 'CAPTAIN',
  'Vice Captain': 'VICE_CAPTAIN',
};

/**
 * `playerRole` is FK-constrained to the `role` lookup table on the backend,
 * which only contains the admin roles (PLAYER / CAPTAIN / VICE_CAPTAIN).
 * Playing role (Batsman/Bowler/All-rounder/Wicket-keeper) is captured in the
 * UI but does NOT go into playerRole — only Wicket-keeper maps to a real
 * backend field (the `wicketKeeper` boolean).
 */
const resolvePlayerRole = (row: NewPlayerRow): string => ADMIN_ROLE_TO_API[row.role];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const makeEmptyRow = (): NewPlayerRow => ({
  rowId: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  firstName: '',
  lastName: '',
  role: 'Player',
  playingRole: '',
  countryCode: DEFAULT_COUNTRY_CODE,
  phoneNo: '',
  emailId: '',
  dob: '',
});

const hasAnyRowContent = (row: NewPlayerRow): boolean =>
  !!(row.firstName.trim() || row.lastName.trim() || row.phoneNo.trim() || row.emailId.trim() || row.dob);

// -------- Excel import --------
// The real backend template (`generatePlayerUploadFileForTournamentId`) is a workbook
// with ONE SHEET PER TEAM (sheet name = team name), each pre-populated with that team's
// current roster in First Name / Last Name / Player Role columns only — no team-ID
// column, no DOB/phone/email. Import must therefore: read every sheet, resolve each
// sheet to a team by name (not just take the first sheet), and treat any row matching
// an existing roster name as already-present (skip it — the backend has no "update an
// existing player" path via this endpoint, only create).

interface ImportTeamGroup {
  teamSnapshotId: string;
  teamLabel: string;
  sheetName: string;
  rows: NewPlayerRow[];
  hasExistingCaptain: boolean;
  hasExistingViceCaptain: boolean;
}

interface ImportRowIssue {
  teamLabel: string;
  rowNum: number;
  reason: string;
}

interface ImportTeamStat {
  teamLabel: string;
  sheetName: string;
  added: number;
  duplicatesInFile: number;
  duplicatesOnRoster: number;
}

interface ImportSummary {
  fileName: string;
  teamStats: ImportTeamStat[];
  unmatchedSheets: string[];
  ambiguousSheets: string[];
  issues: ImportRowIssue[];
}

const IMPORT_HEADER_ALIASES: Record<
  'firstName' | 'lastName' | 'role' | 'playingRole' | 'dob' | 'countryCode' | 'phoneNo' | 'emailId',
  string[]
> = {
  firstName: ['playerFirstName', 'Player First Name', 'First Name', 'firstName', 'first_name'],
  lastName: ['playerLastName', 'Player Last Name', 'Last Name', 'lastName', 'last_name'],
  role: ['role', 'Role', 'playerRole', 'Player Role', 'Admin Role'],
  playingRole: ['playingRole', 'Playing Role', 'Player Type', 'playing_role'],
  dob: ['dob', 'DOB', 'Date of Birth', 'dateOfBirth', 'date_of_birth'],
  countryCode: ['countryCode', 'Country Code', 'country_code', 'ISD Code', 'Dial Code'],
  phoneNo: ['phoneNo', 'Phone Number', 'Mobile Number', 'Mobile', 'phone', 'Phone', 'phone_no'],
  emailId: ['emailId', 'Email', 'Email Id', 'Email Address', 'email', 'email_id'],
};

const normalizeHeader = (s: string) =>
  String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s*:_()-]+/g, '');

const getCell = (row: Record<string, unknown>, keys: readonly string[]): unknown => {
  for (const k of keys) {
    const expected = normalizeHeader(k);
    for (const rowKey of Object.keys(row)) {
      if (normalizeHeader(rowKey) === expected) {
        const v = row[rowKey];
        if (v != null && v !== '') return v;
      }
    }
  }
  return undefined;
};

const getCellStr = (row: Record<string, unknown>, keys: readonly string[]): string => {
  const v = getCell(row, keys);
  return v != null ? String(v).trim() : '';
};

const ROLE_ALIASES: Record<string, NewPlayerRow['role']> = {
  player: 'Player',
  captain: 'Captain',
  c: 'Captain',
  vicecaptain: 'Vice Captain',
  vc: 'Vice Captain',
};

const PLAYING_ROLE_ALIASES: Record<string, NewPlayerRow['playingRole']> = {
  batsman: 'Batsman',
  bat: 'Batsman',
  bowler: 'Bowler',
  allrounder: 'All-rounder',
  wicketkeeper: 'Wicket-keeper',
  wk: 'Wicket-keeper',
  keeper: 'Wicket-keeper',
};

const resolveImportedRole = (raw: string): NewPlayerRow['role'] =>
  ROLE_ALIASES[normalizeHeader(raw)] ?? 'Player';

const resolveImportedPlayingRole = (raw: string): NewPlayerRow['playingRole'] =>
  PLAYING_ROLE_ALIASES[normalizeHeader(raw)] ?? '';

// Days between the Excel/SheetJS date epoch (1899-12-30) and the JS epoch (1970-01-01).
const EXCEL_EPOCH_OFFSET = 25569;

const excelSerialToIso = (serial: number): string | undefined => {
  if (!Number.isFinite(serial) || serial < 1 || serial > 60000) return undefined;
  const d = new Date((serial - EXCEL_EPOCH_OFFSET) * 86400 * 1000);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString().slice(0, 10);
};

const isoFromParts = (year: number, month: number, day: number): string | undefined => {
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return undefined;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const parseImportedDob = (raw: unknown): string => {
  if (raw == null || raw === '') return '';
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? '' : raw.toISOString().slice(0, 10);
  }
  if (typeof raw === 'number') return excelSerialToIso(raw) ?? '';
  const s = String(raw).trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dmy = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (dmy) {
    const a = Number(dmy[1]);
    const b = Number(dmy[2]);
    const year = Number(dmy[3]);
    // Ambiguous when both parts are <=12: assume day-first (matches this app's India-first default).
    const [day, month] = a > 12 ? [a, b] : b > 12 ? [b, a] : [a, b];
    const iso = isoFromParts(year, month, day);
    if (iso) return iso;
  }
  const fallback = new Date(s);
  if (Number.isNaN(fallback.getTime())) return '';
  // Extract via local getters (not toISOString/UTC getters) — `s` was parsed in the
  // browser's local timezone, so round-tripping through UTC here can shift the
  // calendar day backward for any positive-UTC-offset timezone (e.g. India).
  return isoFromParts(fallback.getFullYear(), fallback.getMonth() + 1, fallback.getDate()) ?? '';
};

const splitPhone = (
  rawCombined: unknown,
  rawCountryCode: unknown
): { countryCode: string; phoneNo: string } => {
  const ccFromColumn = String(rawCountryCode ?? '').replace(/\D/g, '');
  const combined = String(rawCombined ?? '').trim();
  if (!combined) return { countryCode: ccFromColumn || DEFAULT_COUNTRY_CODE, phoneNo: '' };

  if (ccFromColumn && findCountry(ccFromColumn)) {
    const digits = combined.replace(/\D/g, '');
    const stripped = digits.startsWith(ccFromColumn) ? digits.slice(ccFromColumn.length) : digits;
    return { countryCode: ccFromColumn, phoneNo: stripped };
  }

  if (combined.startsWith('+')) {
    const m = combined.match(/^\+?(\d{1,3})[\s-]?(\d{6,12})$/);
    if (m) return { countryCode: m[1], phoneNo: m[2] };
  }

  return { countryCode: DEFAULT_COUNTRY_CODE, phoneNo: combined.replace(/\D/g, '') };
};

const nameKeyOf = (firstName: string, lastName: string): string =>
  `${firstName.trim()}|${lastName.trim()}`.toLowerCase();

/** Leadership already present in a roster — used to stop a second Captain/VC being added. */
const deriveLeadership = (members: IMember[]): { hasCaptain: boolean; hasViceCaptain: boolean } => ({
  hasCaptain: members.some((m) => m.isCaptain || (m.role ?? '').toUpperCase() === 'CAPTAIN'),
  hasViceCaptain: members.some(
    (m) => m.isViceCaptain || (m.role ?? '').toUpperCase() === 'VICE_CAPTAIN'
  ),
});

const importRowFromExcel = (
  raw: Record<string, unknown>
): { row: NewPlayerRow | null; hadAnyContent: boolean } => {
  const firstName = getCellStr(raw, IMPORT_HEADER_ALIASES.firstName);
  const lastName = getCellStr(raw, IMPORT_HEADER_ALIASES.lastName);
  const roleRaw = getCellStr(raw, IMPORT_HEADER_ALIASES.role);
  const playingRoleRaw = getCellStr(raw, IMPORT_HEADER_ALIASES.playingRole);
  const dob = parseImportedDob(getCell(raw, IMPORT_HEADER_ALIASES.dob));
  const emailId = getCellStr(raw, IMPORT_HEADER_ALIASES.emailId);
  const { countryCode, phoneNo } = splitPhone(
    getCell(raw, IMPORT_HEADER_ALIASES.phoneNo),
    getCell(raw, IMPORT_HEADER_ALIASES.countryCode)
  );

  const hadAnyContent = !!(firstName || lastName || phoneNo || emailId || dob);
  if (!firstName && !lastName) return { row: null, hadAnyContent };

  const row: NewPlayerRow = {
    ...makeEmptyRow(),
    firstName,
    lastName,
    role: roleRaw ? resolveImportedRole(roleRaw) : 'Player',
    playingRole: playingRoleRaw ? resolveImportedPlayingRole(playingRoleRaw) : '',
    countryCode,
    phoneNo,
    emailId,
    dob,
  };
  return { row, hadAnyContent };
};

const buildPlayerRequest = (row: NewPlayerRow): IBulkPlayerRequest => {
  const digits = row.phoneNo.replace(/\D/g, '');
  return {
    playerFirstName: row.firstName.trim(),
    playerLastName: row.lastName.trim(),
    playerRole: resolvePlayerRole(row),
    captain: row.role === 'Captain',
    wicketKeeper: row.playingRole === 'Wicket-keeper',
    phoneNo: digits || null,
    countryCode: digits ? Number(row.countryCode) : null,
    emailId: row.emailId.trim() || null,
    dob: row.dob || null,
  };
};

const mapTeams = (entries: IDivisionTeamEntry[] | undefined): TeamOption[] => {
  if (!Array.isArray(entries)) return [];
  const seen = new Set<string>();
  const out: TeamOption[] = [];
  for (const entry of entries) {
    const baseTeamId = entry.team?.teamId ?? entry.teamId ?? '';
    const teamSnapshotId = (entry.teamSnapshotId ?? '').toString().trim();
    const id = teamSnapshotId || baseTeamId;
    if (!id) continue;
    const dedupeKey = teamSnapshotId || baseTeamId;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    const label =
      entry.team?.teamName ??
      entry.teamName ??
      (entry as { teamSnapshotName?: string }).teamSnapshotName ??
      'Unnamed Team';
    out.push({
      id,
      label,
      baseTeamId: baseTeamId || undefined,
      teamSnapshotId: teamSnapshotId || undefined,
    });
  }
  return out;
};

const buildDivisionOptions = (divisions: IDivisionResponse[]): DivisionOption[] =>
  divisions.map((division) => {
    const childGroups = division.childDivisions ?? [];
    const groups: GroupOption[] =
      childGroups.length > 0
        ? childGroups.map((group) => ({
            id: group.divisionId,
            name: group.divisionName,
            teams: mapTeams(group.teams),
          }))
        : [
            {
              id: division.divisionId,
              name: division.divisionName,
              teams: mapTeams(division.teams),
            },
          ];
    return {
      id: division.divisionId,
      name: division.divisionName,
      groups,
    };
  });

const extractServerError = (err: unknown): { status?: number; message: string } => {
  const axiosErr = err as {
    response?: { status?: number; data?: { message?: string; error?: string } | string };
    message?: string;
  };
  const status = axiosErr?.response?.status;
  const data = axiosErr?.response?.data;
  const serverMsg = typeof data === 'string' ? data : data?.message ?? data?.error ?? '';
  return { status, message: serverMsg || axiosErr?.message || '' };
};

const validateRow = (row: NewPlayerRow): RowErrors => {
  const errs: RowErrors = {};
  const hasAnyContent = hasAnyRowContent(row);
  if (hasAnyContent && !row.firstName.trim()) errs.firstName = 'Required';
  if (hasAnyContent && !row.lastName.trim()) errs.lastName = 'Required';
  const phoneErr = validatePhone(row.phoneNo, row.countryCode);
  if (phoneErr) errs.phoneNo = phoneErr;
  if (row.emailId.trim() && !EMAIL_RE.test(row.emailId.trim())) errs.emailId = 'Invalid email';
  if (hasAnyContent) {
    const dobErr = validateDob(row.dob);
    if (dobErr) errs.dob = dobErr;
  }
  return errs;
};

const AddPlayersStepV2 = ({
  leagueId,
  tournamentId,
  teamSnapshots: _teamSnapshots,
  onNext,
}: AddPlayersStepV2Props) => {
  const [activeTab, setActiveTab] = useState<'new' | 'existing'>('new');
  const [divisionId, setDivisionId] = useState<string>('');
  const [groupId, setGroupId] = useState<string>('');
  const [teamId, setTeamId] = useState<string>('');
  const [rows, setRows] = useState<NewPlayerRow[]>([makeEmptyRow()]);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExisting, setSelectedExisting] = useState<IPlayerSearchResult[]>([]);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [savingImport, setSavingImport] = useState(false);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);
  const [importGroups, setImportGroups] = useState<ImportTeamGroup[]>([]);
  const [importTouched, setImportTouched] = useState<Record<string, boolean>>({});
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: divisionsRaw } = useQuery({
    queryKey: [QUERY_KEY.getDivisions, tournamentId],
    queryFn: () => fetchDivisions(tournamentId ?? ''),
    enabled: !!tournamentId,
  });

  const divisionOptions = useMemo(() => {
    const list = extractConfiguredDivisionList(divisionsRaw);
    return Array.isArray(list) ? buildDivisionOptions(list) : [];
  }, [divisionsRaw]);

  // Flattened, deduped list of every team in the tournament (all divisions/groups) —
  // used to resolve an imported workbook's per-team sheet names, since that mapping
  // has to span the whole tournament, not just whichever team is currently selected.
  const allTournamentTeams = useMemo(() => {
    const seen = new Set<string>();
    const out: { teamSnapshotId: string; label: string }[] = [];
    for (const division of divisionOptions) {
      for (const group of division.groups) {
        for (const t of group.teams) {
          const tsid = t.teamSnapshotId ?? t.id;
          if (!tsid || seen.has(tsid)) continue;
          seen.add(tsid);
          out.push({ teamSnapshotId: tsid, label: t.label });
        }
      }
    }
    return out;
  }, [divisionOptions]);

  const groupOptions = useMemo(
    () => divisionOptions.find((d) => d.id === divisionId)?.groups ?? [],
    [divisionOptions, divisionId]
  );

  const teamOptions = useMemo(
    () => groupOptions.find((g) => g.id === groupId)?.teams ?? [],
    [groupOptions, groupId]
  );

  useEffect(() => {
    if (divisionOptions.length === 1 && !divisionId) setDivisionId(divisionOptions[0].id);
  }, [divisionOptions, divisionId]);

  useEffect(() => {
    if (groupOptions.length === 1 && !groupId) setGroupId(groupOptions[0].id);
  }, [groupOptions, groupId]);

  useEffect(() => {
    if (teamOptions.length === 1 && !teamId) setTeamId(teamOptions[0].id);
  }, [teamOptions, teamId]);

  const { data: searchResults = [], isFetching: searching } = useQuery({
    queryKey: ['searchPlayers', searchTerm],
    queryFn: () => searchPlayers(searchTerm),
    enabled: searchTerm.trim().length > 1,
  });

  const selectedTeam = teamOptions.find((t) => t.id === teamId);
  const teamSnapshotId = selectedTeam?.teamSnapshotId ?? selectedTeam?.id;

  const queryClient = useQueryClient();

  // Current roster for the selected team snapshot. Loads after team selection.
  const {
    data: roster = [],
    isFetching: rosterLoading,
    isError: rosterError,
  } = useQuery<IMember[]>({
    queryKey: [QUERY_KEY.getAllPlayers, teamSnapshotId],
    queryFn: () => fetchTeamSnapshotPlayers(teamSnapshotId!),
    enabled: !!teamSnapshotId,
  });

  const rosterIds = useMemo(() => new Set(roster.map((m) => m.playerId)), [roster]);
  const existingLeadership = useMemo(() => deriveLeadership(roster), [roster]);

  // Drop any selected-existing entries that have since landed on the roster
  // (e.g. partial-success retry, or roster refetch after an add).
  useEffect(() => {
    if (rosterIds.size === 0) return;
    setSelectedExisting((prev) => {
      const next = prev.filter((p) => !rosterIds.has(p.playerId));
      return next.length === prev.length ? prev : next;
    });
  }, [rosterIds]);

  const invalidateRoster = () => {
    if (teamSnapshotId) {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY.getAllPlayers, teamSnapshotId],
      });
    }
  };

  const [removingPlayerId, setRemovingPlayerId] = useState<string | null>(null);

  const handleRemoveFromRoster = async (member: IMember) => {
    if (!teamSnapshotId) return;
    const name = `${member.playerFirstName} ${member.playerLastName}`.trim() || 'this player';
    if (!window.confirm(`Remove ${name} from this team?`)) return;

    setRemovingPlayerId(member.playerId);
    try {
      await removePlayerFromTeamSnapshot(teamSnapshotId, member.playerId);
      toast.success(`${name} removed from team`);
      // Optimistic-ish: prune locally so UI updates while invalidation refetches.
      queryClient.setQueryData<IMember[]>(
        [QUERY_KEY.getAllPlayers, teamSnapshotId],
        (prev) => (prev ? prev.filter((p) => p.playerId !== member.playerId) : prev)
      );
      invalidateRoster();
    } catch (err) {
      const { message } = extractServerError(err);
      toast.error(message || `Could not remove ${name}`);
    } finally {
      setRemovingPlayerId(null);
    }
  };

  const updateRow = (rowId: string, patch: Partial<NewPlayerRow>) => {
    setRows((prev) => prev.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)));
  };
  const removeRow = (rowId: string) => {
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((r) => r.rowId !== rowId)));
  };
  const addRow = () => setRows((prev) => [...prev, makeEmptyRow()]);

  const toggleExistingPlayer = (player: IPlayerSearchResult) => {
    setSelectedExisting((prev) => {
      const exists = prev.some((p) => p.playerId === player.playerId);
      return exists ? prev.filter((p) => p.playerId !== player.playerId) : [...prev, player];
    });
  };

  const rowErrors = useMemo(() => {
    const map: Record<string, RowErrors> = {};
    for (const r of rows) map[r.rowId] = validateRow(r);
    return map;
  }, [rows]);

  const filtersComplete = !!divisionId && !!groupId && !!teamId;

  const newFlowValid = useMemo(() => {
    const filled = rows.filter(hasAnyRowContent);
    if (filled.length === 0) return false;
    for (const r of filled) {
      const e = rowErrors[r.rowId];
      if (e && (e.firstName || e.lastName || e.phoneNo || e.emailId || e.dob)) return false;
    }
    const captains = filled.filter((r) => r.role === 'Captain').length;
    const viceCaptains = filled.filter((r) => r.role === 'Vice Captain').length;
    if (captains > 1 || viceCaptains > 1) return false;
    if (existingLeadership.hasCaptain && captains > 0) return false;
    if (existingLeadership.hasViceCaptain && viceCaptains > 0) return false;
    return true;
  }, [rows, rowErrors, existingLeadership]);

  const handleDownloadTemplate = async () => {
    if (!tournamentId) return;
    try {
      const { data } = await Api.get<Blob>(
        `${QUERY_KEY.generatePlayerUploadFileForTournamentId}/${tournamentId}`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bulk-player-upload-template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download template. Try again.');
    }
  };

  const handleImportFile = async (file: File) => {
    if (!/\.(xlsx|xls)$/i.test(file.name)) {
      toast.error('Please upload a .xlsx or .xls file');
      return;
    }
    const MAX_IMPORT_BYTES = 5 * 1024 * 1024;
    if (file.size > MAX_IMPORT_BYTES) {
      toast.error('File is too large (max 5MB). Trim it down or split into multiple uploads.');
      return;
    }
    if (allTournamentTeams.length === 0) {
      toast.error('No teams found for this tournament yet — add teams before importing players.');
      return;
    }

    setImporting(true);
    setImportSummary(null);
    try {
      const buf = await file.arrayBuffer();
      const workbook = XLSX.read(buf, { cellDates: true });
      if (workbook.SheetNames.length === 0) {
        toast.error('Could not find a sheet in this file.');
        return;
      }

      // Sheet name -> team(s) with that exact (normalized) name. A workbook sheet is
      // matched to a team purely by name (the template has no team-ID column), so an
      // ambiguous match (two teams sharing a name) is reported rather than guessed.
      const byNormalizedLabel = new Map<string, { teamSnapshotId: string; label: string }[]>();
      for (const t of allTournamentTeams) {
        const key = normalizeHeader(t.label);
        const list = byNormalizedLabel.get(key) ?? [];
        list.push(t);
        byNormalizedLabel.set(key, list);
      }

      const unmatchedSheets: string[] = [];
      const ambiguousSheets: string[] = [];
      const teamStats: ImportTeamStat[] = [];
      const issues: ImportRowIssue[] = [];
      const newGroups: ImportTeamGroup[] = [];
      let anySheetHadKnownColumns = false;

      for (const sheetName of workbook.SheetNames) {
        const sheet = workbook.Sheets[sheetName];
        if (!sheet) continue;
        const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
        if (rawRows.length === 0) continue;

        const firstRowKeys = Object.keys(rawRows[0]);
        const hasKnownColumn = Object.values(IMPORT_HEADER_ALIASES)
          .flat()
          .some((alias) => firstRowKeys.some((k) => normalizeHeader(k) === normalizeHeader(alias)));
        if (!hasKnownColumn) continue; // e.g. an "Instructions" tab — not an error, just not player data
        anySheetHadKnownColumns = true;

        const matches = byNormalizedLabel.get(normalizeHeader(sheetName)) ?? [];
        if (matches.length === 0) {
          unmatchedSheets.push(sheetName);
          continue;
        }
        if (matches.length > 1) {
          ambiguousSheets.push(sheetName);
          continue;
        }
        const team = matches[0];

        let roster: IMember[] = [];
        try {
          roster = await fetchTeamSnapshotPlayers(team.teamSnapshotId);
        } catch {
          issues.push({
            teamLabel: team.label,
            rowNum: 0,
            reason: "Could not verify this team's current roster — duplicate detection skipped for this sheet",
          });
        }
        const { hasCaptain, hasViceCaptain } = deriveLeadership(roster);
        const alreadyKnownNames = new Set<string>([
          ...roster.map((m) => nameKeyOf(m.playerFirstName ?? '', m.playerLastName ?? '')),
          // Also guard against re-importing the same file/team twice in one session.
          ...(importGroups
            .find((g) => g.teamSnapshotId === team.teamSnapshotId)
            ?.rows.map((r) => nameKeyOf(r.firstName, r.lastName)) ?? []),
        ]);

        const seenInSheet = new Set<string>();
        const teamRows: NewPlayerRow[] = [];
        let added = 0;
        let duplicatesInFile = 0;
        let duplicatesOnRoster = 0;

        rawRows.forEach((raw, idx) => {
          const rowNum = idx + 2; // +1 for header row, +1 for 1-based numbering
          const { row, hadAnyContent } = importRowFromExcel(raw);
          if (!row) {
            if (hadAnyContent) {
              issues.push({ teamLabel: team.label, rowNum, reason: 'Missing first/last name — skipped' });
            }
            return;
          }
          const key = nameKeyOf(row.firstName, row.lastName);
          if (seenInSheet.has(key)) {
            duplicatesInFile += 1;
            issues.push({
              teamLabel: team.label,
              rowNum,
              reason: 'Duplicate of another row in this sheet — skipped',
            });
            return;
          }
          seenInSheet.add(key);
          if (alreadyKnownNames.has(key)) {
            duplicatesOnRoster += 1;
            issues.push({
              teamLabel: team.label,
              rowNum,
              reason: `Already on ${team.label}'s roster — skipped (edits to existing players aren't picked up by import)`,
            });
            return;
          }
          const errs = validateRow(row);
          const errMsgs = [errs.dob, errs.phoneNo, errs.emailId].filter(Boolean);
          if (errMsgs.length > 0) {
            issues.push({ teamLabel: team.label, rowNum, reason: `Imported with issues: ${errMsgs.join('; ')}` });
          }
          teamRows.push(row);
          added += 1;
        });

        teamStats.push({ teamLabel: team.label, sheetName, added, duplicatesInFile, duplicatesOnRoster });
        if (teamRows.length > 0) {
          newGroups.push({
            teamSnapshotId: team.teamSnapshotId,
            teamLabel: team.label,
            sheetName,
            rows: teamRows,
            hasExistingCaptain: hasCaptain,
            hasExistingViceCaptain: hasViceCaptain,
          });
        }
      }

      if (!anySheetHadKnownColumns) {
        toast.error(
          'Could not recognize any columns in this file. Download the template below and use its headers.',
          { duration: 8000 }
        );
        return;
      }

      if (newGroups.length > 0) {
        setImportGroups((prev) => {
          const byId = new Map(prev.map((g) => [g.teamSnapshotId, g]));
          for (const g of newGroups) {
            const existing = byId.get(g.teamSnapshotId);
            byId.set(g.teamSnapshotId, existing ? { ...g, rows: [...existing.rows, ...g.rows] } : g);
          }
          return Array.from(byId.values());
        });
        setImportTouched((t) => {
          const next = { ...t };
          for (const g of newGroups) {
            for (const r of g.rows) {
              (['firstName', 'lastName', 'phoneNo', 'emailId', 'dob'] as const).forEach((f) => {
                next[`${r.rowId}-${f}`] = true;
              });
            }
          }
          return next;
        });
      }

      setImportSummary({ fileName: file.name, teamStats, unmatchedSheets, ambiguousSheets, issues });

      const totalAdded = teamStats.reduce((s, t) => s + t.added, 0);
      if (totalAdded === 0) {
        toast.error('No new players could be imported — see the summary for details.', { duration: 7000 });
      } else {
        const teamCount = newGroups.length;
        toast.success(
          `Import complete: ${totalAdded} new player(s) across ${teamCount} team${teamCount === 1 ? '' : 's'} — review below before saving.`,
          { duration: 6000 }
        );
      }
    } catch (err) {
      console.error('[AddPlayersV2] import parse error', err);
      toast.error('Could not read this file. Make sure it is a valid Excel file and try again.');
    } finally {
      setImporting(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleImportFile(file);
    e.target.value = '';
  };

  const handleImportDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleImportFile(file);
  };

  const updateImportRow = (teamSnapshotId: string, rowId: string, patch: Partial<NewPlayerRow>) => {
    setImportGroups((prev) =>
      prev.map((g) =>
        g.teamSnapshotId === teamSnapshotId
          ? { ...g, rows: g.rows.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)) }
          : g
      )
    );
  };

  const removeImportRow = (teamSnapshotId: string, rowId: string) => {
    setImportGroups((prev) =>
      prev
        .map((g) =>
          g.teamSnapshotId === teamSnapshotId
            ? { ...g, rows: g.rows.filter((r) => r.rowId !== rowId) }
            : g
        )
        .filter((g) => g.rows.length > 0)
    );
  };

  const dismissImportGroup = (teamSnapshotId: string) => {
    setImportGroups((prev) => prev.filter((g) => g.teamSnapshotId !== teamSnapshotId));
  };

  const importRowErrors = useMemo(() => {
    const map: Record<string, RowErrors> = {};
    for (const g of importGroups) for (const r of g.rows) map[r.rowId] = validateRow(r);
    return map;
  }, [importGroups]);

  const isImportGroupValid = (g: ImportTeamGroup): boolean => {
    const filled = g.rows.filter(hasAnyRowContent);
    if (filled.length === 0) return false;
    for (const r of filled) {
      const e = importRowErrors[r.rowId];
      if (e && (e.firstName || e.lastName || e.phoneNo || e.emailId || e.dob)) return false;
    }
    const newCaptains = filled.filter((r) => r.role === 'Captain').length;
    const newVCs = filled.filter((r) => r.role === 'Vice Captain').length;
    if (newCaptains > 1 || newVCs > 1) return false;
    if (g.hasExistingCaptain && newCaptains > 0) return false;
    if (g.hasExistingViceCaptain && newVCs > 0) return false;
    return true;
  };

  const importShowError = (rowId: string, field: keyof RowErrors): string | undefined => {
    if (!importTouched[`${rowId}-${field}`]) return undefined;
    return importRowErrors[rowId]?.[field];
  };

  const handleSaveImported = async () => {
    if (importGroups.length === 0 || !tournamentId) return;

    setImportTouched((t) => {
      const next = { ...t };
      for (const g of importGroups) {
        for (const r of g.rows) {
          (['firstName', 'lastName', 'phoneNo', 'emailId', 'dob'] as const).forEach((f) => {
            next[`${r.rowId}-${f}`] = true;
          });
        }
      }
      return next;
    });

    const validGroups = importGroups.filter(isImportGroupValid);
    const invalidCount = importGroups.length - validGroups.length;

    if (validGroups.length === 0) {
      toast.error('Fix the highlighted rows before saving.');
      return;
    }

    setSavingImport(true);
    try {
      let finalGroups = validGroups;

      // Per-team league-wide duplicate preflight, same check the manual "Add New
      // Players" flow already runs before saving.
      if (leagueId) {
        const preflight = await Promise.allSettled(
          validGroups.map((g) =>
            checkDuplicatesInLeague({
              leagueId,
              teamId: g.teamSnapshotId,
              players: g.rows.filter(hasAnyRowContent).map((r) => ({
                playerId: null,
                playerFirstName: r.firstName.trim(),
                playerLastName: r.lastName.trim(),
              })),
              playerIds: [],
            })
          )
        );
        const blocked: string[] = [];
        finalGroups = validGroups.filter((g, i) => {
          const res = preflight[i];
          if (res.status === 'fulfilled' && (res.value.duplicateInLeague ?? []).length > 0) {
            blocked.push(g.teamLabel);
            return false;
          }
          return true;
        });
        if (blocked.length > 0) {
          toast.error(
            `Skipped ${blocked.join(', ')} — matching name(s) already exist elsewhere in this league. Review those teams' rows.`,
            { duration: 8000 }
          );
        }
      }

      if (finalGroups.length === 0) {
        toast.error('All teams were blocked by duplicate checks.');
        return;
      }

      const payload: IPlayerForTeamsItem[] = finalGroups.map((g) => ({
        teamSnapshotId: g.teamSnapshotId,
        playerRequestList: g.rows.filter(hasAnyRowContent).map(buildPlayerRequest),
      }));

      await bulkUploadPlayersForTeams(tournamentId, { playerForTeamsList: payload });

      const savedIds = new Set(finalGroups.map((g) => g.teamSnapshotId));
      const savedPlayerCount = finalGroups.reduce(
        (s, g) => s + g.rows.filter(hasAnyRowContent).length,
        0
      );
      setImportGroups((prev) => prev.filter((g) => !savedIds.has(g.teamSnapshotId)));
      savedIds.forEach((tsid) => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY.getAllPlayers, tsid] });
      });

      const remaining = invalidCount > 0 ? ` ${invalidCount} team(s) still need fixes.` : '';
      toast.success(
        `Saved ${savedPlayerCount} player(s) across ${finalGroups.length} team(s).${remaining}`,
        { duration: 6000 }
      );
    } catch (err) {
      const { message } = extractServerError(err);
      toast.error(message || 'Failed to save imported players');
    } finally {
      setSavingImport(false);
    }
  };

  const handleSaveNew = async () => {
    const allTouched: Record<string, boolean> = {};
    rows.forEach((r) => {
      allTouched[`${r.rowId}-firstName`] = true;
      allTouched[`${r.rowId}-lastName`] = true;
      allTouched[`${r.rowId}-phoneNo`] = true;
      allTouched[`${r.rowId}-emailId`] = true;
      allTouched[`${r.rowId}-dob`] = true;
    });
    setTouched(allTouched);

    if (!newFlowValid) {
      toast.error('Fix the highlighted rows before saving');
      return;
    }
    const captains = rows.filter((r) => r.role === 'Captain').length;
    const vcs = rows.filter((r) => r.role === 'Vice Captain').length;
    if (captains > 1) {
      toast.error('Only one Captain allowed per team');
      return;
    }
    if (vcs > 1) {
      toast.error('Only one Vice Captain allowed per team');
      return;
    }
    if (captains > 0 && existingLeadership.hasCaptain) {
      toast.error('This team already has a Captain — remove that role from your new rows.');
      return;
    }
    if (vcs > 0 && existingLeadership.hasViceCaptain) {
      toast.error('This team already has a Vice Captain — remove that role from your new rows.');
      return;
    }
    const playerRequestList = rows
      .filter((r) => r.firstName.trim() || r.lastName.trim())
      .map(buildPlayerRequest);

    // Pre-flight: warn about name-based duplicates in the league before save.
    if (leagueId) {
      try {
        const dup = await checkDuplicatesInLeague({
          leagueId,
          teamId: teamSnapshotId!,
          players: playerRequestList.map((p) => ({
            playerId: null,
            playerFirstName: p.playerFirstName ?? '',
            playerLastName: p.playerLastName ?? '',
          })),
          playerIds: [],
        });
        const dupList = dup.duplicateInLeague ?? [];
        if (dupList.length > 0) {
          const names = dupList
            .map((d) => `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim() || 'Unnamed')
            .slice(0, 5)
            .join(', ');
          const more = dupList.length > 5 ? ` (+${dupList.length - 5} more)` : '';
          toast.error(
            `Already in league: ${names}${more}. Remove these rows or rename them, then try again.`,
            { duration: 7000 }
          );
          return;
        }
      } catch (preErr) {
        console.warn('[AddPlayersV2] duplicate pre-flight check failed', preErr);
      }
    }

    const payload: IPlayerForTeamsItem = {
      teamSnapshotId: teamSnapshotId!,
      playerRequestList,
    };
    try {
      await bulkUploadPlayersForTeams(tournamentId!, { playerForTeamsList: [payload] });
      toast.success('Players added successfully');
      setRows([makeEmptyRow()]);
      invalidateRoster();
    } catch (err) {
      const { status, message } = extractServerError(err);
      const blob = message.toLowerCase();
      const isConstraint =
        blob.includes('constraint') ||
        blob.includes('duplicate') ||
        blob.includes('unique');
      if (status === 500 && isConstraint) {
        toast.error(
          'Could not save — likely a duplicate email or phone already on CricCenter. Try “Add Existing Players” to attach them, or use different contact details.',
          { duration: 8000 }
        );
      } else if (status === 409) {
        toast.error(message || 'Duplicate detected. Adjust the entries and try again.');
      } else {
        toast.error(message || 'Failed to save players');
      }
    }
  };

  const handleSaveExisting = async () => {
    if (selectedExisting.length === 0) {
      toast.error('Select at least one player');
      return;
    }
    const tsId = teamSnapshotId!;

    // Fire all attaches in parallel; track per-player success/failure.
    const results = await Promise.allSettled(
      selectedExisting.map((p) => addPlayerToTeamSnapshot(tsId, p.playerId))
    );

    const failed: { name: string; reason: string }[] = [];
    results.forEach((r, idx) => {
      if (r.status === 'rejected') {
        const player = selectedExisting[idx];
        const { message } = extractServerError(r.reason);
        failed.push({
          name: `${player.playerFirstName} ${player.playerLastName}`.trim() || 'Unnamed',
          reason: message || 'Unknown error',
        });
      }
    });

    const succeeded = results.length - failed.length;

    if (failed.length === 0) {
      toast.success(`Attached ${succeeded} player${succeeded === 1 ? '' : 's'}`);
      setSelectedExisting([]);
      invalidateRoster();
      return;
    }

    if (succeeded === 0) {
      toast.error(
        `Could not attach players. ${failed[0].reason || 'They may already be in this team.'}`,
        { duration: 7000 }
      );
      return;
    }

    // Partial: keep the failed ones selected so user can retry / inspect.
    const failedNames = new Set(failed.map((f) => f.name));
    setSelectedExisting((prev) =>
      prev.filter((p) =>
        failedNames.has(`${p.playerFirstName} ${p.playerLastName}`.trim() || 'Unnamed')
      )
    );
    toast.error(
      `Attached ${succeeded}, failed ${failed.length}: ${failed
        .slice(0, 3)
        .map((f) => f.name)
        .join(', ')}${failed.length > 3 ? ` (+${failed.length - 3} more)` : ''}`,
      { duration: 8000 }
    );
    invalidateRoster();
  };

  const handleSave = async () => {
    if (!tournamentId) {
      toast.error('Tournament context missing');
      return;
    }
    if (!teamSnapshotId) {
      toast.error('Select Division, Group and Team');
      return;
    }
    setSaving(true);
    try {
      if (activeTab === 'new') {
        await handleSaveNew();
      } else {
        await handleSaveExisting();
      }
    } finally {
      setSaving(false);
    }
  };

  const showError = (rowId: string, field: keyof RowErrors): string | undefined => {
    if (!touched[`${rowId}-${field}`]) return undefined;
    return rowErrors[rowId]?.[field];
  };

  return (
    <div className="add-players-v2">
      <div className="add-players-v2__header">
        <button
          type="button"
          className="add-players-v2__back"
          onClick={onNext}
          aria-label="Back to tournament teams"
        >
          <img src={Images.white_back_arrow} alt="Back" />
        </button>
        <span className="add-players-v2__title">Add Players</span>
      </div>

      <div className="add-players-v2__body">
        <div className="add-players-v2__filters">
          <div className="add-players-v2__field">
            <label className="add-players-v2__label">
              Division <span className="req">*</span>
            </label>
            <FormDropdown
              value={divisionId}
              placeholder="Select Division"
              onChange={(v) => {
                setDivisionId(v);
                setGroupId('');
                setTeamId('');
              }}
              options={divisionOptions.map((d) => ({ label: d.name, value: d.id }))}
            />
          </div>
          <div className="add-players-v2__field">
            <label className="add-players-v2__label">
              Group <span className="req">*</span>
            </label>
            <FormDropdown
              value={groupId}
              placeholder={divisionId ? 'Select Group' : 'Pick a Division first'}
              disabled={!divisionId}
              onChange={(v) => {
                setGroupId(v);
                setTeamId('');
              }}
              options={groupOptions.map((g) => ({ label: g.name, value: g.id }))}
            />
          </div>
          <div className="add-players-v2__field">
            <label className="add-players-v2__label">
              Team <span className="req">*</span>
            </label>
            <FormDropdown
              value={teamId}
              placeholder={groupId ? 'Select Team' : 'Pick a Group first'}
              disabled={!groupId}
              onChange={setTeamId}
              options={teamOptions.map((t) => ({ label: t.label, value: t.id }))}
            />
          </div>
        </div>

        {teamSnapshotId && (
          <div className="add-players-v2__roster">
            <div className="add-players-v2__roster-header">
              <div className="add-players-v2__roster-title">
                Current Roster
                {!rosterLoading && !rosterError && (
                  <span className="roster-count">{roster.length}</span>
                )}
              </div>
              {selectedTeam && (
                <span className="add-players-v2__roster-team">{selectedTeam.label}</span>
              )}
            </div>

            {rosterLoading ? (
              <div className="add-players-v2__hint">Loading team roster…</div>
            ) : rosterError ? (
              <div className="add-players-v2__hint is-error">
                Couldn't load roster. New players you add will still attach to this team.
              </div>
            ) : roster.length === 0 ? (
              <div className="add-players-v2__roster-empty">
                No players on this team yet. Add some below.
              </div>
            ) : (
              <div className="add-players-v2__roster-grid">
                {roster.map((m) => {
                  const fullName =
                    `${m.playerFirstName ?? ''} ${m.playerLastName ?? ''}`.trim() ||
                    'Unnamed player';
                  const initials =
                    `${(m.playerFirstName?.[0] ?? '').toUpperCase()}${(m.playerLastName?.[0] ?? '').toUpperCase()}` ||
                    '?';
                  const isCaptain =
                    m.isCaptain || (m.role ?? '').toUpperCase() === 'CAPTAIN';
                  const isVC =
                    m.isViceCaptain || (m.role ?? '').toUpperCase() === 'VICE_CAPTAIN';
                  const removing = removingPlayerId === m.playerId;
                  return (
                    <div
                      className={`add-players-v2__roster-card ${removing ? 'is-removing' : ''}`}
                      key={m.playerId}
                    >
                      <div className="roster-avatar" aria-hidden="true">
                        {initials || '?'}
                      </div>
                      <div className="roster-body">
                        <div className="roster-name" title={fullName}>
                          {fullName}
                        </div>
                        <div className="roster-meta" title={m.playerCode || m.playerId}>
                          ID · {m.playerCode || m.playerId.slice(0, 8)}
                        </div>
                      </div>
                      {(isCaptain || isVC) && (
                        <span
                          className={`roster-chip ${
                            isCaptain ? 'chip-captain' : 'chip-vc'
                          }`}
                        >
                          {isCaptain ? 'C' : 'VC'}
                        </span>
                      )}
                      <button
                        type="button"
                        className="roster-remove-btn"
                        aria-label={`Remove ${fullName}`}
                        title="Remove from team"
                        disabled={removing}
                        onClick={() => handleRemoveFromRoster(m)}
                      >
                        {removing ? '…' : '×'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="add-players-v2__import">
          <div className="add-players-v2__import-header">
            <div className="add-players-v2__panel-title">Import Players from Excel</div>
            <div className="add-players-v2__panel-sub">
              Upload the tournament template — one sheet per team. New players are staged for
              review below; players already on a team's roster are skipped automatically.
            </div>
          </div>
          <div
            className={`add-players-v2__dropzone ${isDraggingFile ? 'is-dragging' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              if (!importing) setIsDraggingFile(true);
            }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={handleImportDrop}
          >
            <span className="dropzone-text">
              Drag &amp; drop the filled-in template here, or{' '}
              <button
                type="button"
                className="dropzone-browse"
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
              >
                browse
              </button>
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xls,.xlsx"
              hidden
              disabled={importing}
              onChange={handleFileInputChange}
            />
            <button
              type="button"
              className="dropzone-template"
              onClick={handleDownloadTemplate}
              disabled={!tournamentId}
            >
              Download template
            </button>
          </div>
          {importing && <div className="add-players-v2__hint">Reading file…</div>}
          {importSummary && (() => {
            const totalAdded = importSummary.teamStats.reduce((s, t) => s + t.added, 0);
            const totalOnRoster = importSummary.teamStats.reduce((s, t) => s + t.duplicatesOnRoster, 0);
            const totalDupInFile = importSummary.teamStats.reduce((s, t) => s + t.duplicatesInFile, 0);
            const teamsWithNew = importSummary.teamStats.filter((t) => t.added > 0).length;
            const actionableIssues = importSummary.issues.filter(
              (iss) => !iss.reason.startsWith('Already on')
            );
            const routineIssues = importSummary.issues.filter((iss) =>
              iss.reason.startsWith('Already on')
            );

            return (
              <div className="add-players-v2__import-summary">
                <div className="import-summary-line">
                  <span>
                    <strong>{importSummary.fileName}</strong>: {totalAdded} new player
                    {totalAdded === 1 ? '' : 's'} ready across {teamsWithNew} team
                    {teamsWithNew === 1 ? '' : 's'}
                    {totalOnRoster ? `, ${totalOnRoster} already on roster` : ''}
                    {totalDupInFile ? `, ${totalDupInFile} duplicate in sheet` : ''}
                  </span>
                  <button
                    type="button"
                    className="import-summary-dismiss"
                    onClick={() => setImportSummary(null)}
                    aria-label="Dismiss import summary"
                  >
                    ×
                  </button>
                </div>

                {importSummary.teamStats.length > 0 && (
                  <div className="import-stats">
                    {importSummary.teamStats.map((t) => {
                      const teamActionable = actionableIssues.filter((i) => i.teamLabel === t.teamLabel);
                      return (
                        <div className="import-stat-row" key={`${t.teamLabel}-${t.sheetName}`}>
                          <span className="import-stat-team">{t.teamLabel}</span>
                          <span className={`stat-pill ${t.added > 0 ? 'stat-new' : 'stat-none'}`}>
                            {t.added} new
                          </span>
                          {t.duplicatesOnRoster > 0 && (
                            <span className="stat-pill stat-muted">
                              {t.duplicatesOnRoster} already on roster
                            </span>
                          )}
                          {t.duplicatesInFile > 0 && (
                            <span className="stat-pill stat-warn">
                              {t.duplicatesInFile} duplicate in sheet
                            </span>
                          )}
                          {teamActionable.length > 0 && (
                            <span className="stat-pill stat-flag">
                              {teamActionable.length} need review
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {importSummary.unmatchedSheets.length > 0 && (
                  <div className="import-summary-note">
                    Sheet name(s) didn't match any team in this tournament, so they were skipped:{' '}
                    {importSummary.unmatchedSheets.join(', ')}. Rename the sheet to match the team
                    name exactly and re-upload.
                  </div>
                )}
                {importSummary.ambiguousSheets.length > 0 && (
                  <div className="import-summary-note">
                    Multiple teams share a name with these sheets, so they were skipped to avoid
                    misrouting players: {importSummary.ambiguousSheets.join(', ')}.
                  </div>
                )}

                {actionableIssues.length > 0 && (
                  <ul className="import-summary-issues">
                    {actionableIssues.slice(0, 20).map((iss, i) => (
                      <li key={i}>
                        {iss.teamLabel}
                        {iss.rowNum > 0 ? `, Row ${iss.rowNum}` : ''}: {iss.reason}
                      </li>
                    ))}
                    {actionableIssues.length > 20 && <li>+{actionableIssues.length - 20} more…</li>}
                  </ul>
                )}

                {routineIssues.length > 0 && (
                  <details className="import-summary-routine">
                    <summary>
                      {routineIssues.length} routine roster match{routineIssues.length === 1 ? '' : 'es'}{' '}
                      skipped — click to view
                    </summary>
                    <ul className="import-summary-issues">
                      {routineIssues.slice(0, 50).map((iss, i) => (
                        <li key={i}>
                          {iss.teamLabel}, Row {iss.rowNum}: {iss.reason}
                        </li>
                      ))}
                      {routineIssues.length > 50 && <li>+{routineIssues.length - 50} more…</li>}
                    </ul>
                  </details>
                )}
              </div>
            );
          })()}

          {importGroups.length > 0 && (
            <div className="add-players-v2__import-groups">
              {importGroups.map((group) => {
                const groupValid = isImportGroupValid(group);
                return (
                  <div className="import-group" key={group.teamSnapshotId}>
                    <div className="import-group-header">
                      <span className="import-group-title">{group.teamLabel}</span>
                      <span className="import-group-count">
                        {group.rows.length} new player{group.rows.length === 1 ? '' : 's'}
                      </span>
                      {!groupValid && <span className="import-group-flag">Needs review</span>}
                      <button
                        type="button"
                        className="import-group-dismiss"
                        onClick={() => dismissImportGroup(group.teamSnapshotId)}
                        aria-label={`Remove ${group.teamLabel} from this import`}
                      >
                        Remove
                      </button>
                    </div>
                    <div className="add-players-v2__table-scroll">
                      <table className="add-players-v2__table">
                        <thead>
                          <tr>
                            <th className="col-action" />
                            <th className="col-num">#</th>
                            <th className="col-first">
                              First Name <span className="req">*</span>
                            </th>
                            <th className="col-last">
                              Last Name <span className="req">*</span>
                            </th>
                            <th className="col-role">Role</th>
                            <th className="col-playing">Playing Role</th>
                            <th className="col-dob">
                              Date of Birth <span className="req">*</span>
                            </th>
                            <th className="col-mobile">Mobile Number</th>
                            <th className="col-email">Email</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.rows.map((row, idx) => {
                            const firstErr = importShowError(row.rowId, 'firstName');
                            const lastErr = importShowError(row.rowId, 'lastName');
                            const phoneErr = importShowError(row.rowId, 'phoneNo');
                            const emailErr = importShowError(row.rowId, 'emailId');
                            const dobErr = importShowError(row.rowId, 'dob');
                            const todayIso = new Date().toISOString().slice(0, 10);
                            return (
                              <tr key={row.rowId}>
                                <td className="col-action">
                                  <button
                                    type="button"
                                    className="cell-del-btn"
                                    aria-label="Remove row"
                                    onClick={() => removeImportRow(group.teamSnapshotId, row.rowId)}
                                    title="Remove row"
                                  >
                                    ×
                                  </button>
                                </td>
                                <td className="col-num">{idx + 1}</td>
                                <td className="col-first">
                                  <input
                                    type="text"
                                    value={row.firstName}
                                    placeholder="First name"
                                    className={firstErr ? 'has-error' : ''}
                                    onBlur={() =>
                                      setImportTouched((t) => ({ ...t, [`${row.rowId}-firstName`]: true }))
                                    }
                                    onChange={(e) =>
                                      updateImportRow(group.teamSnapshotId, row.rowId, {
                                        firstName: e.target.value,
                                      })
                                    }
                                  />
                                  {firstErr && <span className="cell-err">{firstErr}</span>}
                                </td>
                                <td className="col-last">
                                  <input
                                    type="text"
                                    value={row.lastName}
                                    placeholder="Last name"
                                    className={lastErr ? 'has-error' : ''}
                                    onBlur={() =>
                                      setImportTouched((t) => ({ ...t, [`${row.rowId}-lastName`]: true }))
                                    }
                                    onChange={(e) =>
                                      updateImportRow(group.teamSnapshotId, row.rowId, {
                                        lastName: e.target.value,
                                      })
                                    }
                                  />
                                  {lastErr && <span className="cell-err">{lastErr}</span>}
                                </td>
                                <td className="col-role">
                                  <select
                                    value={row.role}
                                    onChange={(e) =>
                                      updateImportRow(group.teamSnapshotId, row.rowId, {
                                        role: e.target.value as NewPlayerRow['role'],
                                      })
                                    }
                                  >
                                    {ROLE_OPTIONS.map((r) => (
                                      <option key={r} value={r}>
                                        {r}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="col-playing">
                                  <select
                                    value={row.playingRole}
                                    onChange={(e) =>
                                      updateImportRow(group.teamSnapshotId, row.rowId, {
                                        playingRole: e.target.value as NewPlayerRow['playingRole'],
                                      })
                                    }
                                  >
                                    <option value="">Select</option>
                                    {PLAYING_ROLE_OPTIONS.map((r) => (
                                      <option key={r} value={r}>
                                        {r}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td className="col-dob">
                                  <input
                                    type="date"
                                    value={row.dob}
                                    max={todayIso}
                                    className={dobErr ? 'has-error' : ''}
                                    onBlur={() =>
                                      setImportTouched((t) => ({ ...t, [`${row.rowId}-dob`]: true }))
                                    }
                                    onChange={(e) =>
                                      updateImportRow(group.teamSnapshotId, row.rowId, { dob: e.target.value })
                                    }
                                  />
                                  {dobErr && <span className="cell-err">{dobErr}</span>}
                                </td>
                                <td className="col-mobile">
                                  <div className="phone-group">
                                    <select
                                      className="phone-cc"
                                      value={row.countryCode}
                                      aria-label="Country code"
                                      onChange={(e) =>
                                        updateImportRow(group.teamSnapshotId, row.rowId, {
                                          countryCode: e.target.value,
                                        })
                                      }
                                    >
                                      {COUNTRIES.map((c) => (
                                        <option key={`${c.iso}-${c.code}`} value={c.code}>
                                          {c.iso} +{c.code}
                                        </option>
                                      ))}
                                    </select>
                                    <input
                                      type="tel"
                                      inputMode="numeric"
                                      value={row.phoneNo}
                                      placeholder="98765 43210"
                                      maxLength={10}
                                      className={`phone-num ${phoneErr ? 'has-error' : ''}`}
                                      onBlur={() =>
                                        setImportTouched((t) => ({ ...t, [`${row.rowId}-phoneNo`]: true }))
                                      }
                                      onChange={(e) =>
                                        updateImportRow(group.teamSnapshotId, row.rowId, {
                                          phoneNo: e.target.value.replace(/\D/g, '').slice(0, 10),
                                        })
                                      }
                                    />
                                  </div>
                                  {phoneErr && <span className="cell-err">{phoneErr}</span>}
                                </td>
                                <td className="col-email">
                                  <input
                                    type="email"
                                    value={row.emailId}
                                    placeholder="email@example.com"
                                    className={emailErr ? 'has-error' : ''}
                                    onBlur={() =>
                                      setImportTouched((t) => ({ ...t, [`${row.rowId}-emailId`]: true }))
                                    }
                                    onChange={(e) =>
                                      updateImportRow(group.teamSnapshotId, row.rowId, {
                                        emailId: e.target.value,
                                      })
                                    }
                                  />
                                  {emailErr && <span className="cell-err">{emailErr}</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
              <div className="add-players-v2__panel-actions">
                <span className="add-players-v2__hint-inline">
                  Fields marked with <span className="req">*</span> are required before saving.
                </span>
                <button
                  type="button"
                  className="add-players-v2__save"
                  onClick={handleSaveImported}
                  disabled={savingImport}
                >
                  {savingImport ? 'Saving…' : 'Save Imported Players'}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="add-players-v2__tabs">
          <button
            type="button"
            className={`add-players-v2__tab ${activeTab === 'new' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('new')}
          >
            <span className="tab-icon">+</span> Add New Players
          </button>
          <button
            type="button"
            className={`add-players-v2__tab ${activeTab === 'existing' ? 'is-active' : ''}`}
            onClick={() => setActiveTab('existing')}
          >
            <span className="tab-icon">+</span> Add Existing Players
          </button>
        </div>

        {activeTab === 'new' ? (
          <div className="add-players-v2__panel">
            <div className="add-players-v2__panel-header">
              <div className="add-players-v2__panel-title">Add New Players</div>
              <div className="add-players-v2__panel-sub">
                Fill in player details below. Fields marked with <span className="req">*</span> are required.
              </div>
            </div>

            <div className="add-players-v2__table-scroll">
              <table className="add-players-v2__table">
                <thead>
                  <tr>
                    <th className="col-action" />
                    <th className="col-num">#</th>
                    <th className="col-first">
                      First Name <span className="req">*</span>
                    </th>
                    <th className="col-last">
                      Last Name <span className="req">*</span>
                    </th>
                    <th className="col-role">Role</th>
                    <th className="col-playing">Playing Role</th>
                    <th className="col-dob">
                      Date of Birth <span className="req">*</span>
                    </th>
                    <th className="col-mobile">Mobile Number</th>
                    <th className="col-email">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const firstErr = showError(row.rowId, 'firstName');
                    const lastErr = showError(row.rowId, 'lastName');
                    const phoneErr = showError(row.rowId, 'phoneNo');
                    const emailErr = showError(row.rowId, 'emailId');
                    const dobErr = showError(row.rowId, 'dob');
                    const today = new Date().toISOString().slice(0, 10);
                    return (
                      <tr key={row.rowId}>
                        <td className="col-action">
                          <button
                            type="button"
                            className="cell-del-btn"
                            aria-label="Delete row"
                            onClick={() => removeRow(row.rowId)}
                            disabled={rows.length === 1}
                            title="Remove row"
                          >
                            ×
                          </button>
                        </td>
                        <td className="col-num">{idx + 1}</td>
                        <td className="col-first">
                          <input
                            type="text"
                            value={row.firstName}
                            placeholder="First name"
                            className={firstErr ? 'has-error' : ''}
                            onBlur={() =>
                              setTouched((t) => ({ ...t, [`${row.rowId}-firstName`]: true }))
                            }
                            onChange={(e) => updateRow(row.rowId, { firstName: e.target.value })}
                          />
                          {firstErr && <span className="cell-err">{firstErr}</span>}
                        </td>
                        <td className="col-last">
                          <input
                            type="text"
                            value={row.lastName}
                            placeholder="Last name"
                            className={lastErr ? 'has-error' : ''}
                            onBlur={() =>
                              setTouched((t) => ({ ...t, [`${row.rowId}-lastName`]: true }))
                            }
                            onChange={(e) => updateRow(row.rowId, { lastName: e.target.value })}
                          />
                          {lastErr && <span className="cell-err">{lastErr}</span>}
                        </td>
                        <td className="col-role">
                          <select
                            value={row.role}
                            onChange={(e) =>
                              updateRow(row.rowId, { role: e.target.value as NewPlayerRow['role'] })
                            }
                          >
                            {ROLE_OPTIONS.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="col-playing">
                          <select
                            value={row.playingRole}
                            onChange={(e) =>
                              updateRow(row.rowId, {
                                playingRole: e.target.value as NewPlayerRow['playingRole'],
                              })
                            }
                          >
                            <option value="">Select</option>
                            {PLAYING_ROLE_OPTIONS.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="col-dob">
                          <input
                            type="date"
                            value={row.dob}
                            max={today}
                            className={dobErr ? 'has-error' : ''}
                            onBlur={() =>
                              setTouched((t) => ({ ...t, [`${row.rowId}-dob`]: true }))
                            }
                            onChange={(e) => updateRow(row.rowId, { dob: e.target.value })}
                          />
                          {dobErr && <span className="cell-err">{dobErr}</span>}
                        </td>
                        <td className="col-mobile">
                          <div className="phone-group">
                            <select
                              className="phone-cc"
                              value={row.countryCode}
                              aria-label="Country code"
                              onChange={(e) =>
                                updateRow(row.rowId, { countryCode: e.target.value })
                              }
                            >
                              {COUNTRIES.map((c) => (
                                <option key={`${c.iso}-${c.code}`} value={c.code}>
                                  {c.iso} +{c.code}
                                </option>
                              ))}
                            </select>
                            <input
                              type="tel"
                              inputMode="numeric"
                              value={row.phoneNo}
                              placeholder="98765 43210"
                              maxLength={10}
                              className={`phone-num ${phoneErr ? 'has-error' : ''}`}
                              onBlur={() =>
                                setTouched((t) => ({ ...t, [`${row.rowId}-phoneNo`]: true }))
                              }
                              onChange={(e) =>
                                updateRow(row.rowId, {
                                  phoneNo: e.target.value.replace(/\D/g, '').slice(0, 10),
                                })
                              }
                            />
                          </div>
                          {phoneErr && <span className="cell-err">{phoneErr}</span>}
                        </td>
                        <td className="col-email">
                          <input
                            type="email"
                            value={row.emailId}
                            placeholder="email@example.com"
                            className={emailErr ? 'has-error' : ''}
                            onBlur={() =>
                              setTouched((t) => ({ ...t, [`${row.rowId}-emailId`]: true }))
                            }
                            onChange={(e) => updateRow(row.rowId, { emailId: e.target.value })}
                          />
                          {emailErr && <span className="cell-err">{emailErr}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <button type="button" className="add-players-v2__add-row" onClick={addRow}>
              + Add Another Player
            </button>

            <div className="add-players-v2__panel-actions">
              {!filtersComplete && (
                <span className="add-players-v2__hint-inline">
                  Select Division, Group and Team to enable Save
                </span>
              )}
              <button
                type="button"
                className="add-players-v2__save"
                onClick={handleSave}
                disabled={saving || !filtersComplete}
              >
                {saving ? 'Saving…' : 'Save Players'}
              </button>
            </div>
          </div>
        ) : (
          <div className="add-players-v2__panel">
            <div className="add-players-v2__panel-header">
              <div className="add-players-v2__panel-title">Search & Add Existing Player</div>
              <div className="add-players-v2__panel-sub">
                Search players already on CricCenter by name, mobile, or email.
              </div>
            </div>

            <div className="add-players-v2__search">
              <input
                type="text"
                value={searchQuery}
                placeholder="Search Player by Name, Mobile or Email Address"
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setSearchTerm(searchQuery);
                }}
              />
              <button
                type="button"
                className="add-players-v2__search-btn"
                onClick={() => setSearchTerm(searchQuery)}
                disabled={searchQuery.trim().length < 2}
              >
                Search
              </button>
            </div>

            {searching && <div className="add-players-v2__hint">Searching…</div>}
            {!searching && searchTerm && searchResults.length === 0 && (
              <div className="add-players-v2__hint">No players found for “{searchTerm}”.</div>
            )}

            {!searching && searchResults.length > 0 && (
              <>
                <div className="add-players-v2__results-meta">
                  {searchResults.length} {searchResults.length === 1 ? 'result' : 'results'} for
                  <span className="results-query"> "{searchTerm}"</span>
                </div>
                <div className="add-players-v2__results-grid">
                  {searchResults.map((p) => {
                    const onRoster = rosterIds.has(p.playerId);
                    const checked = selectedExisting.some((s) => s.playerId === p.playerId);
                    const fullName = `${p.playerFirstName ?? ''} ${p.playerLastName ?? ''}`.trim() || 'Unnamed player';
                    const initials = `${(p.playerFirstName?.[0] ?? '').toUpperCase()}${(p.playerLastName?.[0] ?? '').toUpperCase()}` || '?';
                    return (
                      <label
                        className={`add-players-v2__result-card ${checked ? 'is-selected' : ''} ${onRoster ? 'is-on-roster' : ''}`}
                        key={p.playerId}
                        title={onRoster ? 'Already on this team' : undefined}
                      >
                        <input
                          type="checkbox"
                          className="result-checkbox"
                          checked={checked}
                          disabled={onRoster}
                          onChange={() => toggleExistingPlayer(p)}
                          aria-label={`Select ${fullName}`}
                        />
                        <div className="result-avatar" aria-hidden="true">
                          {initials || '?'}
                        </div>
                        <div className="result-body">
                          <div className="result-name" title={fullName}>
                            {fullName}
                          </div>
                          <div className="result-meta" title={p.playerCode || p.playerId}>
                            ID · {p.playerCode || p.playerId.slice(0, 8)}
                          </div>
                        </div>
                        {onRoster ? (
                          <span className="result-role-chip role-on-roster">On Team</span>
                        ) : (
                          p.role && (
                            <span className={`result-role-chip role-${(p.role ?? '').toLowerCase().replace(/_/g, '-')}`}>
                              {p.role.replace(/_/g, ' ')}
                            </span>
                          )
                        )}
                      </label>
                    );
                  })}
                </div>
              </>
            )}

            {selectedExisting.length > 0 && (
              <div className="add-players-v2__confirmed">
                <div className="add-players-v2__confirmed-title">
                  Confirmed Players ({selectedExisting.length})
                </div>
                <div className="add-players-v2__chips">
                  {selectedExisting.map((p) => (
                    <span className="add-players-v2__chip" key={p.playerId}>
                      <span className="chip-avatar">
                        {(p.playerFirstName?.[0] ?? '?').toUpperCase()}
                      </span>
                      <span className="chip-name">
                        {p.playerFirstName} {p.playerLastName}
                      </span>
                      <button
                        type="button"
                        aria-label="Remove"
                        onClick={() => toggleExistingPlayer(p)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="add-players-v2__panel-actions">
              {!filtersComplete && (
                <span className="add-players-v2__hint-inline">
                  Select Division, Group and Team to enable Add
                </span>
              )}
              <button
                type="button"
                className="add-players-v2__save"
                onClick={handleSave}
                disabled={saving || !filtersComplete || selectedExisting.length === 0}
              >
                {saving ? 'Adding…' : `Add ${selectedExisting.length || ''} Player${selectedExisting.length === 1 ? '' : 's'}`.trim()}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddPlayersStepV2;
